import Dexie, { type Table } from 'dexie'

import type { Evenement, MetaEntry, Nuit } from '../domain/types'

/**
 * Base locale. Tout reste sur l'appareil : aucune synchronisation, aucun compte.
 *
 * ## Pourquoi aucun booleen n'est indexe
 *
 * IndexedDB n'accepte comme cle que : number, string, Date, ArrayBuffer et
 * Array. Un booleen (`corrigee`, `ignoree`) n'est **pas une cle valide** :
 * Dexie n'echoue pas, il omet simplement l'enregistrement de l'index. Une
 * requete `where('corrigee').equals(true)` renverrait alors systematiquement
 * zero resultat, silencieusement — et les nuits corrigees se feraient ecraser
 * au recalcul suivant sans que rien ne le signale. Ces champs sont donc
 * filtres en memoire, dans `repo.ts`.
 *
 * ## Pourquoi les evenements bruts sont conserves
 *
 * On pourrait ne stocker que les nuits deduites. Mais toute la conception
 * repose sur la possibilite de **rejouer** l'interpretation : changer un seuil,
 * corriger une regle, rattraper une synchro manquee. Sans les faits bruts,
 * chaque ajustement demanderait de refaire les nuits a la main.
 */
export class SuiviSommeilDb extends Dexie {
  evenements!: Table<Evenement, string>
  nuits!: Table<Nuit, string>
  meta!: Table<MetaEntry, string>

  constructor() {
    super('suivi-sommeil')

    this.version(1).stores({
      evenements: 'id, at, type',
      nuits: 'date, debut',
      meta: 'cle',
    })
  }
}

export const db = new SuiviSommeilDb()

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export const META_REGLAGES = 'reglages'
export const META_DERNIERE_SYNCHRO = 'derniereSynchroAt'
export const META_DERNIER_EXPORT = 'dernierExportAt'

export async function lireMeta<T>(cle: string, defaut: T): Promise<T> {
  const ligne = await db.meta.get(cle)
  return ligne === undefined ? defaut : (ligne.valeur as T)
}

export async function ecrireMeta(cle: string, valeur: unknown): Promise<void> {
  await db.meta.put({ cle, valeur })
}

// ---------------------------------------------------------------------------

/**
 * Identifiant aleatoire.
 *
 * `crypto.randomUUID` n'existe que sur origine securisee : en developpement sur
 * l'IP du reseau local, en clair, il est absent. D'ou les deux replis.
 */
export function nouvelId(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  if (c && typeof c.getRandomValues === 'function') {
    const o = c.getRandomValues(new Uint8Array(16))
    return Array.from(o, (n) => n.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
